import { Users, ShieldCheck, Lock } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

export type VisibilityLevel = 'members' | 'admins' | 'private';

const VISIBILITY_CONFIG: Record<VisibilityLevel, { icon: typeof Users; label: string }> = {
  members: { icon: Users, label: 'All Members' },
  admins: { icon: ShieldCheck, label: 'Admins Only' },
  private: { icon: Lock, label: 'Private' },
};

export function getVisibilityIcon(visibility: VisibilityLevel) {
  return VISIBILITY_CONFIG[visibility]?.icon ?? Users;
}

export function getVisibilityLabel(visibility: VisibilityLevel) {
  return VISIBILITY_CONFIG[visibility]?.label ?? 'All Members';
}

interface VisibilityBadgeProps {
  visibility: VisibilityLevel;
  variant?: 'default' | 'outline';
}

export function VisibilityBadge({ visibility, variant = 'default' }: VisibilityBadgeProps) {
  const Icon = getVisibilityIcon(visibility);
  const containerClass = variant === 'outline'
    ? 'border rounded-full px-2.5 py-0.5'
    : '';

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-muted-foreground', containerClass)}>
      <Icon className="h-3 w-3" />
      {getVisibilityLabel(visibility)}
    </span>
  );
}

interface VisibilitySelectProps {
  value: VisibilityLevel;
  onChange: (value: VisibilityLevel) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Levels the user may pick; defaults to all. The current `value` is always
   * listed so an existing setting still renders.
   */
  allowedLevels?: readonly VisibilityLevel[];
}

export function VisibilitySelect({ value, onChange, disabled, className, allowedLevels }: VisibilitySelectProps) {
  return (
    <Select value={value} onValueChange={(v) => onChange(v as VisibilityLevel)} disabled={disabled}>
      <SelectTrigger className={cn(className)}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {(Object.entries(VISIBILITY_CONFIG) as [VisibilityLevel, typeof VISIBILITY_CONFIG[VisibilityLevel]][])
          .filter(([level]) => !allowedLevels || level === value || allowedLevels.includes(level))
          .map(
          ([level, config]) => {
            const Icon = config.icon;
            return (
              <SelectItem key={level} value={level}>
                <div className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  <span>{config.label}</span>
                </div>
              </SelectItem>
            );
          }
        )}
      </SelectContent>
    </Select>
  );
}
